'use client'

import Link from 'next/link'
import { Suspense, useEffect } from 'react'
import { useSidebar } from '~/components/ContentLayoutClient'
import CourseProgressIndicator from '~/components/CourseProgressIndicator'
import FocusModeToggleButton from '~/components/FocusModeToggleButton'
import { CourseModule } from '~/payload-types'
import { SupportedLocales } from '~/types'

export default function MobileSidebar({
  courseId,
  locale,
  modules,
  courseHrefPrefix = '',
}: {
  courseId: string
  locale: SupportedLocales
  modules: CourseModule[]
  courseHrefPrefix?: string
}) {
  const { isMobileOpen: isOpen, setMobileOpen: setIsOpen } = useSidebar()

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed left-0 right-0 top-14 sm:top-16 bottom-8 sm:bottom-9 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <nav
        className={`lg:hidden fixed right-0 top-14 sm:top-16 bottom-8 sm:bottom-9 w-80 max-w-[80vw] bg-white shadow-xl z-40 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6 space-y-6">
          <Suspense fallback={<div className="mb-4 h-12 bg-gray-100 rounded animate-pulse" />}>
            <CourseProgressIndicator courseId={courseId} locale={locale} />
          </Suspense>

          <div className="mb-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Navigation
              </h2>
              <FocusModeToggleButton className="h-7 w-7" />
            </div>
          </div>

          {modules &&
            modules.length > 0 &&
            modules.map((module) => {
              const moduleHref = `${courseHrefPrefix}#module-${module.id}`
              return (
                <div key={module.id}>
                  {courseHrefPrefix ? (
                    <Link
                      href={moduleHref}
                      onClick={() => setIsOpen(false)}
                      className="hover:text-primary-80 text-gray-900 font-semibold block mb-2"
                    >
                      {module.title}
                    </Link>
                  ) : (
                    <a
                      href={moduleHref}
                      onClick={() => setIsOpen(false)}
                      className="hover:text-primary-80 text-gray-900 font-semibold block mb-2"
                    >
                      {module.title}
                    </a>
                  )}
                  {module.units && module.units.length > 0 && (
                    <div className="ml-4 flex flex-col gap-1.5">
                      {module.units.map((unit) => {
                        if (typeof unit === 'number') return null

                        const unitHref = `${courseHrefPrefix}#unit-${unit.id}`

                        return (
                          <div key={unit.id}>
                            {courseHrefPrefix ? (
                              <Link
                                href={unitHref}
                                onClick={() => setIsOpen(false)}
                                className="hover:text-primary-80 text-gray-700 text-sm"
                              >
                                {unit.title}
                              </Link>
                            ) : (
                              <a
                                href={unitHref}
                                onClick={() => setIsOpen(false)}
                                className="hover:text-primary-80 text-gray-700 text-sm"
                              >
                                {unit.title}
                              </a>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
        </div>
      </nav>
    </>
  )
}
